<template>
  <div id="client-form">
    <b-row class="row">
      <b-col cols="5">
        <text-input
          :label="`${$t('PHONE_CLI')} 1`"
          v-model='primaryPhoneInput'
          :disabled="!client.new || !edit"
          type="phoneNumber"
          required
          borderbottom
          placeholder=""
        />
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          :label="$t('NATIONAL_ID')"
          v-model="nationalIdInput"
          :disabled="!edit"
          borderbottom
          placeholder=""
          @keyup="search"
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="5">
        <text-input
          ref="firstNameTextInput"
          :label="$t('FIRST_NAME')"
          v-model='firstNameInput'
          required
          :disabled="!client.new || !edit || nationalIdInput.trim() == ''"
          borderbottom
          placeholder=" "
        />
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          ref="lastNameTextInput"
          :label="$t('LAST_NAME')"
          v-model='lastNameInput'
          required
          :disabled="!client.new || !edit || nationalIdInput.trim() == ''"
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="row">
      <b-col cols="5">
        <toggle-box ref="dateOfBirthBox">
          <template v-slot:control>
            <text-input
              v-model="dateOfBirthInput"
              :label="$t('DATE_OF_BIRTH')"
              icon-position="right"
              required
              :disabled="!client.new || !edit || nationalIdInput.trim() == ''"
              borderbottom
              :validate="validateDateOfBirth"
              placeholder=""
            >
              <template v-slot:icon>
                <font-awesome-icon @click="showDateOfBirthBox" class="icon" :icon="faCalendarAlt" />
              </template>
            </text-input>
          </template>
          <template v-slot:content>
            <calendar
              :date="moment(dateOfBirthInput, DISPLAY_DATE_FORMAT).toISOString() || undefined"
              @dateChange="setDateOfBirth"
              :yearRange="[moment().subtract(100, 'year').year(), moment().subtract(15, 'year').year()]"
            />
          </template>
        </toggle-box>
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          :label="`${$t('PHONE_CLI')} 2`"
          v-model='alternativePhoneInput'
          :disabled="!client.new || !edit || nationalIdInput.trim() == ''"
          borderbottom
          placeholder=""
          type="phoneNumber"
        />
      </b-col>
    </b-row>
    <text-input
      ref="addressTextInput"
      :label="$t('ADDRESS')"
      class="margin-bottom"
      v-model="addressInput"
      :required="!isNormalContract"
      :disabled="!edit || nationalIdInput.trim() == ''"
      borderbottom
      placeholder=" "
    />
    <b-row class="margin-bottom">
      <b-col cols="5">
        <div
          class="floating-label"
          :class="{
            active: !!genderInput
          }"
        >
          <select-input
            tabindex="-1"
            :items="genderItems"
            v-model="genderInput"
            :disabled="!edit || nationalIdInput.trim() == ''"
            ref="genderSelectInput"
            borderless
            required
          />
          <span class="highlight"></span>
          <label>{{ $t("GENDER")}}<span>*</span></label>
        </div>
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <text-input
          :label="$t('SOURCE')"
          v-model='order.source'
          disabled
          borderbottom
          placeholder=""
        />
      </b-col>
    </b-row>
    <b-row class="margin-bottom">
      <b-col cols="5">
        <div
          class="floating-label required"
          :class="{
            active: !!districtInput
          }"
        >
          <select-input
            tabindex="-1"
            :items="districtItems"
            v-model="districtInput"
            :disabled="!edit || nationalIdInput.trim() == ''"
            ref="districtSelectInput"
            borderless
            :required="!isNormalContract"
          />
          <span class="highlight"></span>
          <label>{{ $t("DISTRICT")}}<span v-if="!isNormalContract">*</span></label>
        </div>
      </b-col>
      <b-col cols="2"></b-col>
      <b-col cols="5">
        <div
          class="floating-label"
          :class="{
            active: !!cityInput
          }"
        >
          <select-input
            tabindex="-1"
            :items="cityItems"
            v-model="cityInput"
            :disabled="!edit || nationalIdInput.trim() == ''"
            ref="citySelectInput"
            borderless
            required
          />
          <span class="highlight"></span>
          <label>{{ $t("CITY")}}<span>*</span></label>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from 'moment';
import { computed, onMounted, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import Calendar from '@/components/Calendar';
import TextInput from '@/components/TextInput';
import ToggleBox from '@/components/ToggleBox';
import SelectInput from '@/components/SelectInput';

import { DATE_FORMAT, DISPLAY_DATE_FORMAT } from '@/consts';

import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import provinces from '@/consts/provinces';

const { useState, useActions } = createNamespacedHelpers('contractNew');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'ClientForm',
  components: {
    Calendar,
    TextInput,
    ToggleBox,
    SelectInput,
    FontAwesomeIcon,
  },
  props: {
    edit: {
      type: Boolean,
      default: true,
    },
    value: Boolean,
  },
  setup(props, { refs, emit }) {
    const { order, client } = useState(['order', 'client', 'assetTypes']);
    const { assetTypes } = useAppState(['assetTypes']);
    const { setClient, getClientByNationalId } = useActions(['setClient', 'getClientByNationalId']);

    const isNormalContract = ref(false);

    const primaryPhoneInput = ref('');

    const nationalIdInput = ref('');
    let searchTimer = null;
    const search = (e) => {
      if (nationalIdInput.value.trim() !== '') {
        if (e.keyCode !== 13) {
          if (searchTimer !== null) {
            clearTimeout(searchTimer);
            searchTimer = null;
          }
          searchTimer = setTimeout(() => {
            getClientByNationalId(nationalIdInput.value);
          }, 2000);
        } else {
          getClientByNationalId(nationalIdInput.value);
          clearTimeout(searchTimer);
          searchTimer = null;
        }
      } else {
        clearTimeout(searchTimer);
        searchTimer = null;
      }
    };

    const firstNameInput = ref('');
    const lastNameInput = ref('');

    const dateOfBirthInput = ref('');
    const validateDateOfBirth = (val) => {
      const date = moment(val, DISPLAY_DATE_FORMAT, true);

      if (date.isValid()) {
        const currentDate = moment();
        if (currentDate.diff(date, 'years') >= 15) {
          return '';
        }
        return 'INVALID_OLD_ENOUGH';
      }
      return 'INVALID_DATE_OF_BIRTH';
    };

    const alternativePhoneInput = ref('');
    const addressInput = ref('');

    const genderInput = ref('');
    const genderItems = [
      {
        text: 'Nam',
        value: 'male',
      },
      {
        text: 'Nữ',
        value: 'female',
      }
    ];

    const districtInput = ref('');
    const cityInput = ref(Object.keys(provinces)[1]);

    const cityItems = ref(Object.keys(provinces).map((province) => ({
      value: province,
      text: province,
    })));

    const districtItems = computed(() => {
      const districts = provinces[cityInput.value];

      if (districts === undefined) {
        return [];
      }
      return districts.map((district) => ({
        value: district,
        text: district,
      }));
    });

    const showDateOfBirthBox = () => {
      if (props.edit && client.value.new) {
        refs.dateOfBirthBox.toggle(true);
      }
    };
    const setDateOfBirth = (val) => {
      dateOfBirthInput.value = moment(val, DATE_FORMAT).format(DISPLAY_DATE_FORMAT);
      refs.dateOfBirthBox.toggle(false);
    };

    const valid = computed(() => {
      let conditions = [
        firstNameInput.value.trim() !== '',
        lastNameInput.value.trim() !== '',
        primaryPhoneInput.value.trim() !== '',
        validateDateOfBirth(dateOfBirthInput.value) === '',
        nationalIdInput.value.trim() !== '',
        genderInput.value === 'male' || genderInput.value === 'female',
        cityInput.value !== undefined && cityInput.value.trim() !== '',
      ];

      if (!isNormalContract) {
        conditions = [
          ...conditions,
          addressInput.value.trim() !== '',
          districtInput.value !== undefined && districtInput.value.trim() !== '',
        ];
      }
      return !conditions.includes(false);
    });

    watch(valid, () => emit('input', valid.value));

    const setData = () => {
      const data = {
        firstName: firstNameInput.value,
        lastName: lastNameInput.value,
        primaryPhone: primaryPhoneInput.value,
        alternativePhone: alternativePhoneInput.value,
        dateOfBirth: dateOfBirthInput.value,
        nationalId: nationalIdInput.value,
        gender: genderInput.value,
        address: addressInput.value,
        district: districtInput.value,
        city: cityInput.value,
      };
      setClient(data);
    };

    const setDataFromState = () => {
      primaryPhoneInput.value = client.value.primaryPhone;
      nationalIdInput.value = client.value.nationalId;
      firstNameInput.value = client.value.firstName;
      lastNameInput.value = client.value.lastName;
      dateOfBirthInput.value = client.value.dateOfBirth;
      alternativePhoneInput.value = client.value.alternativePhone;
      addressInput.value = client.value.address;
      genderInput.value = client.value.gender;
      districtInput.value = client.value.district;

      if (client.value.city) {
        cityInput.value = client.value.city;
      }
    };

    // For check step
    onMounted(() => {
      setDataFromState();
      refs.firstNameTextInput.validateInput();
      refs.lastNameTextInput.validateInput();
      refs.genderSelectInput.validateInput();
    });

    watch(client, setDataFromState);
    watch(order, () => {
      const assetType = assetTypes.value.find((item) => item.description === order.value.assetType);
      isNormalContract.value = !['Bike', 'Car', 'Truck', 'Realestate'].includes(assetType.name);

      if (!isNormalContract.value) {
        refs.addressTextInput.validateInput();
        refs.districtSelectInput.validateInput();
        refs.citySelectInput.validateInput();
      }
    });

    return {
      isNormalContract,
      valid,
      order,
      client,
      setData,
      primaryPhoneInput,
      nationalIdInput,
      search,
      firstNameInput,
      lastNameInput,
      dateOfBirthInput,
      validateDateOfBirth,
      alternativePhoneInput,
      addressInput,
      genderInput,
      genderItems,
      districtInput,
      cityInput,
      cityItems,
      districtItems,
      DATE_FORMAT,
      DISPLAY_DATE_FORMAT,
      faCalendarAlt,
      showDateOfBirthBox,
      setDateOfBirth,
      moment,
    };
  },
};
</script>


<style lang='scss'>
@import "~@/assets/scss/colors";

#client-form {

  .row {
    margin-bottom: 30px;
  }

  .margin-bottom {
    margin-bottom: 40px;
  }

  .floating-label {
    width: 100%;
    position: relative;
    bottom: 10px;
    .select-input {
      width: 100%;
      small {
        position: absolute;
      }
      .display {
        border-bottom: 1px solid $alto;
        padding-left: 0px;
      }
      &:focus {
        outline: none;
        &~label {
          top:-12px;
          font-size:0.825em;
          color:$blueNcs;
        }
      }

      &:focus{
        &~.highlight{
          width: 100%;
        }
      }

      &.disabled {
        pointer-events: none;
        .display {
          border-bottom: 1px solid $silver;
        }
      }
    }
    label {
      color: $grey;
      font-size:14px;
      font-weight: bold;
      position:absolute;
      pointer-events:none;
      left:0px;
      top:13px;
      transition:0.2s ease all;
      -moz-transition:0.4s ease all;
      -webkit-transition:0.4s ease all;
      span {
        color: $geranium;
      }
    }

    .highlight {
      content:'';
      display: block;
      position: absolute;
      left:0;
      width: 0;
      height: 2px;
      background: $blueNcs;
      transition: all 200ms ease-out;
    }

    &.disabled {
      label {
        color: $silver;
      }
      &:focus, &:not(:placeholder-shown):valid {
        &~label {
          top:-12px;
          font-size: 0.825em;
          color:$blueNcs;
        }
      }
    }

    &.active {
      .select-input{
        &:focus, &:not(.value) {
        outline: none;
          &~label {
            top:-12px;
            font-size:0.825em;
            color:$blueNcs;
          }
        }
      }
    };
  }
}
</style>
